'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

// Define the context type
interface AudioRecorderContextType {
  isRecording: boolean;
  isInitializing: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  addRecordingCompleteListener: (listener: (blob: Blob) => void) => () => void; // Returns unsubscriber
  addErrorListener: (listener: (message: string) => void) => () => void; // Returns unsubscriber
  addAudioChunkListener: (listener: (chunk: Uint8Array) => void) => () => void; // Returns unsubscriber
  getAnalyser: () => any | null; // Returns the analyser node
  errorMessage: string | null;
  clearErrorMessage: () => void;
}

// Create the context
const AudioRecorderContext = createContext<AudioRecorderContextType | undefined>(undefined);

// Provider props type
interface AudioRecorderProviderProps {
  children: React.ReactNode;
}

// Define constants outside the component
const MIN_AUDIO_DURATION_MS = 500;
const MAX_AUDIO_DURATION_MS = 60000;

export const AudioRecorderProvider: React.FC<AudioRecorderProviderProps> = ({ children }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const recorderRef = useRef<any>(null); // Use 'any' for the Recorder library
  const analyserRef = useRef<any>(null);
  const recordingContextRef = useRef<AudioContext | null>(null);
  const maxAudioTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastRecordStartRef = useRef<number>(0);
  const audioChunksRef = useRef<Uint8Array[]>([]);
  // Track listeners for when recording completes
  const recordingCompleteListenersRef = useRef<Set<(blob: Blob) => void>>(new Set());
  const errorListenersRef = useRef<Set<(message: string) => void>>(new Set());
  const audioChunkListenersRef = useRef<Set<(chunk: Uint8Array) => void>>(new Set());

  // Initialize the recorder
  useEffect(() => {
    const initializeRecorder = async () => {
      if (recorderRef.current) return; // Already initialized
      
      setIsInitializing(true);
      try {
        // Load the recorder script globally
        const script = document.createElement("script");
        script.src = "https://cdn.chotto.chat/js/opus-encdec/recorder.min.js";
        script.async = true;
        await new Promise<void>((resolve) => {
          script.onload = () => resolve();
          document.body.appendChild(script);
        });

        if (typeof window.Recorder === "undefined") {
          throw new Error("Recorder library not loaded");
        }

        // Apply monkey patch if needed
        if (!window.Recorder.prototype._isPatched) {
          window.Recorder.prototype.encodeAudio = async function (
            audioFloat32Array: Float32Array
          ): Promise<Blob> {
            return new Promise((resolve, reject) => {
              const originalOndataavailable = this.ondataavailable;
              const originalOnstart = this.onstart;
              const originalOnstop = this.onstop;

              const encodedChunks: any[] = [];

              this.ondataavailable = (typedArray: any) => {
                encodedChunks.push(typedArray);
              };

              this.onstart = () => {
                if (this.encoder && this.encoder.postMessage) {
                  this.encoder.postMessage({
                    command: "encode",
                    buffers: [audioFloat32Array],
                  });
                }
              };

              this.onstop = () => {
                const opusBlob = new Blob(encodedChunks, {
                  type: "audio/ogg; codecs=opus",
                });

                this.ondataavailable = originalOndataavailable;
                this.onstart = originalOnstart;
                this.onstop = originalOnstop;

                resolve(opusBlob);
              };

              this.start()
                .then(() => {
                  this.stop();
                })
                .catch(reject);
            });
          };
          window.Recorder.prototype._isPatched = true;
        }

        // Create AudioContext
        try {
          recordingContextRef.current = new (window.AudioContext ||
            window.webkitAudioContext)({ sampleRate: 16000 });
        } catch (e) {
          console.warn("Failed to create AudioContext with 16kHz, using default.", e);
          recordingContextRef.current = new (window.AudioContext ||
            window.webkitAudioContext)();
        }

        const context = recordingContextRef.current;

        // Resume if suspended
        if (context.state === 'suspended') {
          await context.resume();
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const sourceNode = context.createMediaStreamSource(stream);

        const analyserNode = context.createAnalyser();
        analyserNode.fftSize = 64;
        sourceNode.connect(analyserNode);
        analyserRef.current = analyserNode;

        const config = {
          numberOfChannels: 1,
          encoderSampleRate: 16000,
          encoderFrameSize: 20,
          maxFramesPerPage: 40,
          encoderComplexity: 6,
          encoderApplication: 2048,
          originalSampleRate: context.sampleRate,
          streamPages: true,
          sourceNode: sourceNode,
        };

        recorderRef.current = new window.Recorder(config);

        recorderRef.current.ondataavailable = (typedArray: Uint8Array) => {
          audioChunksRef.current.push(typedArray);
          
          // Notify all registered chunk listeners
          audioChunkListenersRef.current.forEach(listener => listener(typedArray));
        };

        recorderRef.current.onstart = () => {
          console.log("Recording started");
          setIsRecording(true);
          lastRecordStartRef.current = Date.now();
          audioChunksRef.current = [];
          
          if (maxAudioTimerRef.current) clearTimeout(maxAudioTimerRef.current);
          maxAudioTimerRef.current = setTimeout(
            stopRecording,
            MAX_AUDIO_DURATION_MS
          );
        };

        recorderRef.current.onstop = () => {
          console.log("Recording stopped");
          setIsRecording(false);
          if (maxAudioTimerRef.current) clearTimeout(maxAudioTimerRef.current);

          const duration = Date.now() - lastRecordStartRef.current;
          if (duration < MIN_AUDIO_DURATION_MS) {
            const errorMsg = "Recording too short";
            setErrorMessage(errorMsg);
            console.log(`Recording too short (${duration}ms), skipping.`);
            // Notify error listeners
            errorListenersRef.current.forEach(listener => listener(errorMsg));
            return;
          }

          if (audioChunksRef.current.length > 0) {
            const blob = new Blob(audioChunksRef.current, {
              type: "audio/ogg; codecs=opus",
            });
            console.log("Final audio blob size:", blob.size);
            // Notify recording complete listeners
            recordingCompleteListenersRef.current.forEach(listener => listener(blob));
            audioChunksRef.current = [];
          } else {
            const errorMsg = "No audio data captured";
            console.warn("No audio chunks recorded.");
            setErrorMessage(errorMsg);
            // Notify error listeners
            errorListenersRef.current.forEach(listener => listener(errorMsg));
          }
        };

        await recorderRef.current.initialize;
        console.log("Recorder initialized successfully");
      } catch (error: any) {
        console.error("Error initializing recorder:", error);
        setErrorMessage(`Mic Error: ${error.message}`);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeRecorder();

    // Cleanup function
    return () => {
      if (maxAudioTimerRef.current) clearTimeout(maxAudioTimerRef.current);
      if (recorderRef.current && recorderRef.current.close) {
        recorderRef.current.close();
      }
      if (recordingContextRef.current) {
        recordingContextRef.current.close();
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecording || isInitializing || !recorderRef.current) return;
    
    try {
      // Resume AudioContext if suspended
      if (recordingContextRef.current && recordingContextRef.current.state === 'suspended') {
        await recordingContextRef.current.resume();
      }
      
      await recorderRef.current.start();
    } catch (error) {
      console.error("Error starting recording:", error);
      setErrorMessage("Error starting recording");
    }
  }, [isRecording, isInitializing]);

  const stopRecording = useCallback(async () => {
    if (!isRecording || isInitializing || !recorderRef.current) return;
    
    try {
      await recorderRef.current.stop();
    } catch (error) {
      console.error("Error stopping recording:", error);
      setErrorMessage("Error stopping recording");
      setIsRecording(false);
    }
  }, [isRecording, isInitializing]);

  const addRecordingCompleteListener = useCallback((listener: (blob: Blob) => void): () => void => {
    recordingCompleteListenersRef.current.add(listener);
    
    // Return unsubscribe function
    return () => {
      recordingCompleteListenersRef.current.delete(listener);
    };
  }, []);

  const addErrorListener = useCallback((listener: (message: string) => void): () => void => {
    errorListenersRef.current.add(listener);
    
    // Return unsubscribe function
    return () => {
      errorListenersRef.current.delete(listener);
    };
  }, []);

  const addAudioChunkListener = useCallback((listener: (chunk: Uint8Array) => void): () => void => {
    audioChunkListenersRef.current.add(listener);
    
    // Return unsubscribe function
    return () => {
      audioChunkListenersRef.current.delete(listener);
    };
  }, []);

  const getAnalyser = useCallback((): any | null => {
    return analyserRef.current;
  }, []);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const contextValue = {
    isRecording,
    isInitializing,
    startRecording,
    stopRecording,
    addRecordingCompleteListener,
    addErrorListener,
    addAudioChunkListener,
    getAnalyser,
    errorMessage,
    clearErrorMessage
  };

  return (
    <AudioRecorderContext.Provider value={contextValue}>
      {children}
    </AudioRecorderContext.Provider>
  );
};

// Custom hook to use the audio recorder context
export const useAudioRecorderContext = () => {
  const context = useContext(AudioRecorderContext);
  if (context === undefined) {
    throw new Error('useAudioRecorderContext must be used within an AudioRecorderProvider');
  }
  return context;
};